<script setup>
import { ref, computed } from 'vue';
import axios from 'axios';
import { useAlert } from 'dashboard/composables';

const props = defineProps({
  stage: { type: Object, required: true },
  pipeline: { type: Object, required: true },
  accountId: { type: [String, Number], required: true },
});

const emit = defineEmits(['close', 'imported']);

const step = ref(1); // 1: upload · 2: mapping · 3: preview · 4: importing · 5: done
const csvText = ref('');
const fileName = ref('');
const headers = ref([]);
const rows = ref([]);

// Mapeo: clave del campo destino → índice de la columna del CSV
const mapping = ref({
  title: -1,
  phone: -1,
  email: -1,
  name: -1,
});

const progress = ref({ done: 0, total: 0, errors: [] });

function parseCsvLine(line) {
  // CSV parser básico que respeta comillas dobles
  const out = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') { current += '"'; i++; }
      else inQuotes = !inQuotes;
    } else if (ch === ',' && !inQuotes) {
      out.push(current.trim());
      current = '';
    } else {
      current += ch;
    }
  }
  out.push(current.trim());
  return out;
}

function parseCsvText(text) {
  const clean = (text || '').replace(/\r\n/g, '\n').replace(/\r/g, '\n').trim();
  if (!clean) return { headers: [], rows: [] };
  const lines = clean.split('\n').filter(l => l.trim());
  if (!lines.length) return { headers: [], rows: [] };
  const hdrs = parseCsvLine(lines[0]).map(h => h.trim());
  const data = lines.slice(1).map(parseCsvLine);
  return { headers: hdrs, rows: data };
}

function autoMap(hdrs) {
  const m = { title: -1, phone: -1, email: -1, name: -1 };
  hdrs.forEach((h, idx) => {
    const lh = h.toLowerCase().trim();
    if (m.name === -1 && /(nombre|name|cliente|contact)/i.test(lh)) m.name = idx;
    if (m.phone === -1 && /(tel|phone|celular|movil|whats)/i.test(lh)) m.phone = idx;
    if (m.email === -1 && /(email|correo|mail|e-mail)/i.test(lh)) m.email = idx;
    if (m.title === -1 && /(titulo|title|asunto|tema|deal|lead)/i.test(lh)) m.title = idx;
  });
  // Fallback: si no encontró title pero sí name, usar name
  if (m.title === -1 && m.name !== -1) m.title = m.name;
  return m;
}

// Carga dinámica de SheetJS desde CDN solo cuando se necesita (.xlsx)
let xlsxPromise = null;
function loadXlsxLib() {
  if (window.XLSX) return Promise.resolve(window.XLSX);
  if (xlsxPromise) return xlsxPromise;
  xlsxPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/xlsx@0.18.5/dist/xlsx.full.min.js';
    script.onload = () => resolve(window.XLSX);
    script.onerror = () => reject(new Error('No se pudo cargar el lector de Excel'));
    document.head.appendChild(script);
  });
  return xlsxPromise;
}

async function readExcelAsCsv(file) {
  const XLSX = await loadXlsxLib();
  const arrayBuffer = await file.arrayBuffer();
  const workbook = XLSX.read(arrayBuffer, { type: 'array' });
  const firstSheet = workbook.SheetNames[0];
  if (!firstSheet) throw new Error('El Excel no tiene hojas');
  const sheet = workbook.Sheets[firstSheet];
  return XLSX.utils.sheet_to_csv(sheet);
}

async function onFileSelected(e) {
  const file = e.target.files?.[0];
  if (!file) return;
  fileName.value = file.name;
  const lowerName = file.name.toLowerCase();
  const isExcel = lowerName.endsWith('.xlsx') || lowerName.endsWith('.xls') || lowerName.endsWith('.xlsm');

  try {
    if (isExcel) {
      csvText.value = await readExcelAsCsv(file);
    } else {
      csvText.value = await file.text();
    }
    processCsv();
  } catch (err) {
    useAlert(err?.message || 'Error al leer el archivo');
  }
}

function onPasteParse() {
  fileName.value = 'CSV pegado';
  processCsv();
}

function processCsv() {
  const parsed = parseCsvText(csvText.value);
  headers.value = parsed.headers;
  rows.value = parsed.rows;
  if (!headers.value.length) {
    useAlert('CSV vacío o inválido');
    return;
  }
  mapping.value = autoMap(headers.value);
  step.value = 2;
}

const previewRows = computed(() => rows.value.slice(0, 5));

function getCell(row, idx) {
  if (idx < 0 || idx >= row.length) return '';
  return row[idx] || '';
}

function goToPreview() {
  if (mapping.value.title === -1 && mapping.value.name === -1) {
    useAlert('Mapea al menos la columna de Nombre o Título');
    return;
  }
  step.value = 3;
}

async function startImport() {
  step.value = 4;
  progress.value = { done: 0, total: rows.value.length, errors: [] };

  for (let i = 0; i < rows.value.length; i++) {
    const row = rows.value[i];
    try {
      await importRow(row);
    } catch (err) {
      progress.value.errors.push(`Fila ${i + 2}: ${err?.response?.data?.message || err?.message || 'error'}`);
    }
    progress.value.done = i + 1;
  }

  step.value = 5;
  emit('imported');
}

async function importRow(row) {
  const m = mapping.value;
  const name = (m.name >= 0 ? getCell(row, m.name) : '') || (m.title >= 0 ? getCell(row, m.title) : '');
  const phone = m.phone >= 0 ? getCell(row, m.phone) : '';
  const email = m.email >= 0 ? getCell(row, m.email) : '';
  const title = (m.title >= 0 ? getCell(row, m.title) : '') || name || `Lead importado`;

  let contactId = null;

  // Crear contacto si hay nombre/phone/email
  if (name || phone || email) {
    try {
      const contactPayload = { inbox_id: null };
      if (name) contactPayload.name = name;
      if (phone) contactPayload.phone_number = phone.startsWith('+') ? phone : `+${phone.replace(/\D/g, '')}`;
      if (email) contactPayload.email = email;
      const url = `/api/v1/accounts/${props.accountId}/contacts`;
      const { data } = await axios.post(url, contactPayload);
      contactId = data?.payload?.contact?.id || data?.id;
    } catch (e) {
      // Si falla crear contacto (ej. duplicate), seguimos con deal sin contacto
    }
  }

  const dealPayload = {
    deal: {
      pipeline_id: props.pipeline.id,
      pipeline_stage_id: props.stage.id,
      title: title.substring(0, 255),
    },
  };
  if (contactId) dealPayload.deal.contact_id = contactId;

  const dealUrl = `/api/v1/accounts/${props.accountId}/deals`;
  await axios.post(dealUrl, dealPayload);
}

function closeAll() { emit('close'); }
function backStep() { if (step.value > 1) step.value--; }
</script>

<template>
  <div class="ilc-overlay" @click.self="closeAll">
    <div class="ilc-modal">
      <div class="ilc-head">
        <div class="ilc-head-icon">📥</div>
        <div>
          <div class="ilc-head-title">Importar leads (CSV o Excel)</div>
          <div class="ilc-head-sub">
            A la columna <strong>{{ stage.name }}</strong> · embudo {{ pipeline.name }}
          </div>
        </div>
        <button class="ilc-x" @click="closeAll">×</button>
      </div>

      <!-- Stepper -->
      <div class="ilc-steps">
        <div class="ilc-step" :class="{ done: step > 1, active: step === 1 }"><div class="ilc-step-num">1</div><div class="ilc-step-label">Subir CSV</div></div>
        <div class="ilc-step-line" :class="{ done: step > 1 }"></div>
        <div class="ilc-step" :class="{ done: step > 2, active: step === 2 }"><div class="ilc-step-num">2</div><div class="ilc-step-label">Mapear</div></div>
        <div class="ilc-step-line" :class="{ done: step > 2 }"></div>
        <div class="ilc-step" :class="{ done: step > 3, active: step === 3 }"><div class="ilc-step-num">3</div><div class="ilc-step-label">Vista previa</div></div>
        <div class="ilc-step-line" :class="{ done: step > 4 }"></div>
        <div class="ilc-step" :class="{ active: step >= 4 }"><div class="ilc-step-num">{{ step === 5 ? '✓' : '4' }}</div><div class="ilc-step-label">Importar</div></div>
      </div>

      <div class="ilc-body">
        <!-- Step 1: Subir CSV -->
        <div v-if="step === 1">
          <div class="ilc-tip">
            💡 Sube el archivo que exportaste de <strong>Kommo</strong> u otro CRM (acepta <strong>.csv</strong> y <strong>.xlsx</strong>).
            Asegúrate de incluir columnas como <code>nombre</code>, <code>teléfono</code>, <code>email</code>.
          </div>

          <label class="ilc-file-input">
            <input
              type="file"
              accept=".csv,text/csv,.xlsx,.xls,.xlsm,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
              @change="onFileSelected"
              hidden
            />
            <div class="ilc-file-icon">📂</div>
            <div class="ilc-file-text">
              <div class="ilc-file-title">{{ fileName || 'Seleccionar archivo CSV o Excel' }}</div>
              <div class="ilc-file-sub">Acepta <strong>.csv</strong>, <strong>.xlsx</strong>, .xls (incluye exportaciones de Kommo)</div>
            </div>
          </label>

          <div class="ilc-or">— o —</div>

          <div class="ilc-paste">
            <label class="ilc-label">Pegar contenido CSV directamente</label>
            <textarea
              v-model="csvText"
              rows="6"
              placeholder="nombre,telefono,email&#10;María López,8095551234,maria@gmail.com&#10;Juan Pérez,8294567890,juan@hotmail.com"
              class="ilc-textarea"
            ></textarea>
            <button
              class="ilc-btn-primary"
              :disabled="!csvText.trim()"
              @click="onPasteParse"
            >Analizar CSV pegado</button>
          </div>
        </div>

        <!-- Step 2: Mapear columnas -->
        <div v-else-if="step === 2">
          <div class="ilc-tip">
            🎯 Detecté <strong>{{ headers.length }} columnas</strong> y <strong>{{ rows.length }} filas</strong>.
            Empareja cada campo destino con la columna del CSV.
          </div>

          <div class="ilc-map-grid">
            <div class="ilc-map-row">
              <div class="ilc-map-label">📝 Título del lead</div>
              <select v-model.number="mapping.title" class="ilc-select">
                <option :value="-1">— No mapear —</option>
                <option v-for="(h, i) in headers" :key="`t${i}`" :value="i">{{ h }}</option>
              </select>
            </div>
            <div class="ilc-map-row">
              <div class="ilc-map-label">👤 Nombre cliente</div>
              <select v-model.number="mapping.name" class="ilc-select">
                <option :value="-1">— No mapear —</option>
                <option v-for="(h, i) in headers" :key="`n${i}`" :value="i">{{ h }}</option>
              </select>
            </div>
            <div class="ilc-map-row">
              <div class="ilc-map-label">📱 Teléfono</div>
              <select v-model.number="mapping.phone" class="ilc-select">
                <option :value="-1">— No mapear —</option>
                <option v-for="(h, i) in headers" :key="`p${i}`" :value="i">{{ h }}</option>
              </select>
            </div>
            <div class="ilc-map-row">
              <div class="ilc-map-label">📧 Email</div>
              <select v-model.number="mapping.email" class="ilc-select">
                <option :value="-1">— No mapear —</option>
                <option v-for="(h, i) in headers" :key="`e${i}`" :value="i">{{ h }}</option>
              </select>
            </div>
          </div>

          <div class="ilc-tip" style="margin-top:14px; background: rgba(99,102,241,0.06); border-color: rgba(99,102,241,0.30); color: #a5b4fc;">
            💡 Si no mapeas <strong>Título</strong>, usaré el <strong>Nombre cliente</strong> como título del lead.
          </div>
        </div>

        <!-- Step 3: Preview -->
        <div v-else-if="step === 3">
          <div class="ilc-tip">
            👀 Vista previa de las primeras {{ Math.min(5, rows.length) }} filas (de {{ rows.length }} totales).
            Confirma que la información se ve correcta antes de importar.
          </div>

          <table class="ilc-preview">
            <thead>
              <tr>
                <th>#</th>
                <th>Título</th>
                <th>Nombre</th>
                <th>Teléfono</th>
                <th>Email</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, idx) in previewRows" :key="idx">
                <td class="ilc-cell-num">{{ idx + 1 }}</td>
                <td>{{ getCell(row, mapping.title) || getCell(row, mapping.name) || '—' }}</td>
                <td>{{ getCell(row, mapping.name) || '—' }}</td>
                <td>{{ getCell(row, mapping.phone) || '—' }}</td>
                <td>{{ getCell(row, mapping.email) || '—' }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Step 4: Importando -->
        <div v-else-if="step === 4">
          <div class="ilc-loading">
            <div class="ilc-spinner"></div>
            <div class="ilc-loading-text">
              Importando lead {{ progress.done }} de {{ progress.total }}…
            </div>
            <div class="ilc-progress-bar">
              <div class="ilc-progress-fill" :style="{ width: `${(progress.done / progress.total) * 100}%` }"></div>
            </div>
          </div>
        </div>

        <!-- Step 5: Done -->
        <div v-else-if="step === 5">
          <div class="ilc-done">
            <div class="ilc-done-icon">{{ progress.errors.length === 0 ? '✅' : '⚠️' }}</div>
            <div class="ilc-done-title">
              {{ progress.done - progress.errors.length }} {{ progress.done - progress.errors.length === 1 ? 'lead importado' : 'leads importados' }}
            </div>
            <div v-if="progress.errors.length" class="ilc-done-sub">
              {{ progress.errors.length }} {{ progress.errors.length === 1 ? 'error' : 'errores' }}:
              <ul class="ilc-error-list">
                <li v-for="(err, i) in progress.errors.slice(0, 5)" :key="i">{{ err }}</li>
              </ul>
              <div v-if="progress.errors.length > 5" class="ilc-error-more">… y {{ progress.errors.length - 5 }} más</div>
            </div>
            <div v-else class="ilc-done-sub">
              Todos los leads se importaron correctamente a <strong>{{ stage.name }}</strong>.
            </div>
          </div>
        </div>
      </div>

      <div class="ilc-foot">
        <button v-if="step > 1 && step < 4" class="ilc-btn-back" @click="backStep">← Atrás</button>
        <div style="flex:1"></div>
        <button v-if="step === 2" class="ilc-btn-primary" @click="goToPreview">Siguiente →</button>
        <button v-if="step === 3" class="ilc-btn-primary" @click="startImport">
          ✨ Importar {{ rows.length }} leads
        </button>
        <button v-if="step === 5" class="ilc-btn-primary" @click="closeAll">Cerrar</button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.ilc-overlay {
  position: fixed; inset: 0;
  background: rgba(0,0,0,0.70);
  backdrop-filter: blur(8px);
  z-index: 9000;
  display: flex; align-items: center; justify-content: center;
  font-family: -apple-system, system-ui, sans-serif;
  color: #fff;
  padding: 24px;
}
.ilc-modal {
  width: 760px;
  max-width: 96vw;
  max-height: 92vh;
  background: linear-gradient(180deg, rgba(15,15,18,0.98), rgba(8,8,10,0.98));
  border: 1px solid rgba(251,191,36,0.30);
  border-radius: 16px;
  box-shadow: 0 32px 80px rgba(0,0,0,0.70), 0 0 0 1px rgba(251,191,36,0.08), 0 0 50px rgba(251,191,36,0.10);
  display: flex; flex-direction: column;
  overflow: hidden;
  position: relative;
}
.ilc-modal::before { content: ''; position: absolute; top: 0; left: 0; right: 0; height: 1px; background: linear-gradient(90deg, transparent, rgba(251,191,36,0.55), transparent); }

.ilc-head { padding: 18px 22px; display: flex; align-items: center; gap: 14px; border-bottom: 1px solid rgba(255,255,255,0.06); background: linear-gradient(135deg, rgba(251,191,36,0.06), transparent 60%); }
.ilc-head-icon { width: 42px; height: 42px; border-radius: 11px; background: linear-gradient(135deg, rgba(251,191,36,0.20), rgba(217,119,6,0.05)); border: 1px solid rgba(251,191,36,0.45); display: flex; align-items: center; justify-content: center; font-size: 18px; }
.ilc-head-title { font-size: 16px; font-weight: 900; }
.ilc-head-sub { font-size: 11px; color: rgba(255,255,255,0.55); margin-top: 2px; }
.ilc-head-sub strong { color: #fbbf24; }
.ilc-x { margin-left: auto; width: 30px; height: 30px; border-radius: 8px; background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08); color: rgba(255,255,255,0.55); display: flex; align-items: center; justify-content: center; font-size: 16px; cursor: pointer; font-family: inherit; }
.ilc-x:hover { background: rgba(239,68,68,0.10); color: #f87171; border-color: rgba(239,68,68,0.30); }

/* Stepper */
.ilc-steps { padding: 14px 22px; display: flex; align-items: center; gap: 6px; border-bottom: 1px solid rgba(255,255,255,0.04); }
.ilc-step { display: flex; align-items: center; gap: 8px; }
.ilc-step-num { width: 22px; height: 22px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 10px; font-weight: 900; background: rgba(255,255,255,0.06); color: rgba(255,255,255,0.50); border: 1px solid rgba(255,255,255,0.10); flex-shrink: 0; }
.ilc-step.done .ilc-step-num { background: rgba(34,197,94,0.20); color: #6ee7b7; border-color: rgba(34,197,94,0.45); }
.ilc-step.active .ilc-step-num { background: linear-gradient(135deg, #fbbf24, #d97706); color: #18181b; box-shadow: 0 0 0 3px rgba(251,191,36,0.15); }
.ilc-step-label { font-size: 10px; font-weight: 700; color: rgba(255,255,255,0.50); }
.ilc-step.done .ilc-step-label, .ilc-step.active .ilc-step-label { color: #fff; }
.ilc-step-line { flex: 1; height: 2px; background: rgba(255,255,255,0.06); border-radius: 2px; }
.ilc-step-line.done { background: linear-gradient(90deg, rgba(34,197,94,0.45), rgba(34,197,94,0.20)); }

/* Body */
.ilc-body { padding: 20px 22px; overflow-y: auto; flex: 1; }

.ilc-tip {
  background: rgba(251,191,36,0.06);
  border: 1px dashed rgba(251,191,36,0.40);
  border-radius: 10px;
  padding: 10px 14px;
  font-size: 11px;
  color: rgba(255,255,255,0.75);
  line-height: 1.5;
  margin-bottom: 14px;
}
.ilc-tip strong { color: #fbbf24; font-weight: 800; }
.ilc-tip code { background: rgba(0,0,0,0.50); padding: 1px 5px; border-radius: 3px; font-family: ui-monospace, Menlo, monospace; font-size: 10px; color: #fcd34d; }

.ilc-file-input {
  display: flex; align-items: center; gap: 14px;
  padding: 16px 18px;
  background: rgba(255,255,255,0.03);
  border: 2px dashed rgba(251,191,36,0.30);
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.18s;
}
.ilc-file-input:hover { border-color: rgba(251,191,36,0.60); background: rgba(251,191,36,0.04); }
.ilc-file-icon { font-size: 32px; }
.ilc-file-title { font-size: 14px; font-weight: 800; color: #fff; }
.ilc-file-sub { font-size: 10.5px; color: rgba(255,255,255,0.55); margin-top: 3px; }

.ilc-or { text-align: center; font-size: 10px; color: rgba(255,255,255,0.40); margin: 14px 0; letter-spacing: 1px; font-weight: 700; }

.ilc-paste { display: flex; flex-direction: column; gap: 8px; }
.ilc-label { font-size: 11px; font-weight: 700; color: rgba(255,255,255,0.70); }
.ilc-textarea {
  width: 100%;
  padding: 10px 12px;
  background: rgba(0,0,0,0.30);
  border: 1px solid rgba(255,255,255,0.10);
  border-radius: 8px;
  color: #fff;
  font-size: 11.5px;
  font-family: ui-monospace, Menlo, monospace;
  outline: none;
  resize: vertical;
}
.ilc-textarea:focus { border-color: rgba(251,191,36,0.55); background: rgba(251,191,36,0.04); }

.ilc-map-grid { display: grid; grid-template-columns: 1fr; gap: 10px; }
.ilc-map-row { display: flex; align-items: center; gap: 12px; padding: 10px 12px; background: rgba(255,255,255,0.02); border: 1px solid rgba(255,255,255,0.06); border-radius: 9px; }
.ilc-map-label { width: 160px; font-size: 12px; font-weight: 700; color: rgba(255,255,255,0.85); flex-shrink: 0; }
.ilc-select {
  flex: 1;
  padding: 7px 10px;
  background: rgba(0,0,0,0.40);
  border: 1px solid rgba(255,255,255,0.10);
  border-radius: 6px;
  color: #fff;
  font-size: 11.5px;
  font-family: inherit;
  cursor: pointer;
}
.ilc-select:focus { outline: none; border-color: rgba(251,191,36,0.45); }

/* Preview table */
.ilc-preview { width: 100%; border-collapse: collapse; font-size: 11px; }
.ilc-preview th { padding: 8px 10px; text-align: left; font-weight: 800; color: #fbbf24; letter-spacing: 0.4px; text-transform: uppercase; font-size: 9.5px; border-bottom: 1px solid rgba(251,191,36,0.30); }
.ilc-preview td { padding: 9px 10px; border-bottom: 1px solid rgba(255,255,255,0.04); color: rgba(255,255,255,0.85); }
.ilc-preview tr:hover td { background: rgba(255,255,255,0.02); }
.ilc-cell-num { color: rgba(255,255,255,0.40); font-weight: 800; }

/* Loading */
.ilc-loading { text-align: center; padding: 36px 0; }
.ilc-spinner { width: 48px; height: 48px; border: 3px solid rgba(251,191,36,0.20); border-top-color: #fbbf24; border-radius: 50%; animation: ilc-spin 0.7s linear infinite; margin: 0 auto 16px; }
@keyframes ilc-spin { to { transform: rotate(360deg); } }
.ilc-loading-text { font-size: 13px; font-weight: 700; margin-bottom: 14px; }
.ilc-progress-bar { width: 100%; height: 8px; background: rgba(255,255,255,0.06); border-radius: 999px; overflow: hidden; }
.ilc-progress-fill { height: 100%; background: linear-gradient(90deg, #fbbf24, #d97706); transition: width 0.3s; }

/* Done */
.ilc-done { text-align: center; padding: 28px 0; }
.ilc-done-icon { font-size: 48px; margin-bottom: 12px; }
.ilc-done-title { font-size: 18px; font-weight: 900; color: #fff; }
.ilc-done-sub { font-size: 11.5px; color: rgba(255,255,255,0.65); margin-top: 8px; line-height: 1.5; }
.ilc-done-sub strong { color: #fbbf24; }
.ilc-error-list { text-align: left; margin: 8px auto 0; max-width: 480px; font-size: 10.5px; color: rgba(239,68,68,0.85); padding-left: 20px; }
.ilc-error-list li { margin: 3px 0; }
.ilc-error-more { font-size: 10px; color: rgba(255,255,255,0.45); margin-top: 4px; }

/* Footer */
.ilc-foot { padding: 14px 22px; border-top: 1px solid rgba(255,255,255,0.06); background: rgba(0,0,0,0.40); display: flex; align-items: center; gap: 8px; }
.ilc-btn-back, .ilc-btn-primary { padding: 9px 16px; border-radius: 8px; font-size: 12px; font-weight: 800; cursor: pointer; border: none; font-family: inherit; }
.ilc-btn-back { background: rgba(255,255,255,0.04); color: rgba(255,255,255,0.65); border: 1px solid rgba(255,255,255,0.10); }
.ilc-btn-back:hover { background: rgba(255,255,255,0.08); color: #fff; }
.ilc-btn-primary { background: linear-gradient(135deg, #fbbf24, #d97706); color: #18181b; box-shadow: 0 4px 14px rgba(251,191,36,0.40); }
.ilc-btn-primary:hover:not(:disabled) { filter: brightness(1.05); transform: translateY(-1px); }
.ilc-btn-primary:disabled { opacity: 0.45; cursor: not-allowed; }
</style>
